import { applyMiddleware, compose, createStore, Reducer, Store } from 'redux';
// tslint:disable-next-line: match-default-export-name
import createSageMiddleware, { SagaIterator } from 'redux-saga';
import thunk from 'redux-thunk';

import { IHttpApi } from '@cian/http-api/shared';
import { ILogger } from '@cian/logger';

import { IMakeRequest } from '../../utils/request';
import { TActions } from './actions';
import { IAppState, ISerializableAppState, IWidgets } from './app_state';
import { IConfig } from '@cian/config/shared';

export interface IReducerAddedAction {
  type: '@@store/REDUCER_ADDED';
  name: string;
}

interface ICustomReducer extends Reducer<IAppState> {
  addReducer(reducer: Reducer<IAppState>): void;
}

function createCustomReducer(): ICustomReducer {
  const subReducers: Reducer<IAppState>[] = [];

  const reducer = ((state: IAppState, action: TActions) => {
    return subReducers.reduce((acc, subReducer) => {
      return subReducer(acc, action);
    }, state);
  }) as ICustomReducer;

  reducer.addReducer = (subReducer: Reducer<IAppState>) => {
    subReducers.push(subReducer);
  };

  return reducer;
}

export interface IAppStore extends Store<IAppState> {
  runSaga(saga: () => SagaIterator): void;
  addReducer(reducer: Reducer<IAppState>): void;
  serialize(): ISerializableAppState;
}

export interface IAppStoreCreatorProps {
  makeRequest: IMakeRequest;
  httpApi: IHttpApi;
  logger: ILogger;
  config: IConfig;
  initialState: ISerializableAppState;

  optionalParams?: {
    dispachReducerAddedAction?: boolean;
    widgets?: IWidgets | undefined;
  };
}

export function createAppStore({
  makeRequest,
  httpApi,
  logger,
  config,
  initialState,
  optionalParams,
}: IAppStoreCreatorProps): IAppStore {
  const initializedParams = Object.assign(
    {
      dispachReducerAddedAction: true,
      widgets: undefined,
      pikBookingWidgetContext: undefined,
    },
    optionalParams || {},
  );

  const { widgets, dispachReducerAddedAction, pikBookingWidgetContext } = initializedParams;

  const state = {
    ...initialState,
    makeRequest,
    httpApi,
    logger,
    config,
    widgets,
    pikBookingWidgetContext,
  };

  const sagaMiddleware = createSageMiddleware();

  const customReducer = createCustomReducer();

  const composeEnhancers =
    typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      : compose;

  const thunkMiddleware = thunk.withExtraArgument({ httpApi, logger });
  const store = createStore(customReducer, state, composeEnhancers(applyMiddleware(thunkMiddleware, sagaMiddleware)));

  function addReducer(reducer: Reducer<IAppState>) {
    customReducer.addReducer(reducer);

    if (dispachReducerAddedAction) {
      store.dispatch({
        type: '@@store/REDUCER_ADDED',
        name: reducer.name,
      });
    }
  }

  function runSaga(saga: () => SagaIterator) {
    sagaMiddleware.run(saga);
  }

  function serialize(): ISerializableAppState {
    const currentState = Object.assign({}, store.getState());
    delete currentState.makeRequest;
    delete currentState.httpApi;
    delete currentState.logger;
    delete currentState.config;

    return currentState;
  }

  return { ...store, runSaga, addReducer, serialize };
}
